import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, X, Trash2, MessageSquare, Heart, UserPlus, UserCheck, Swords } from 'lucide-react';
import { API } from '../App';

const POLL_MS = 60000; // 60s

const formatTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const secs = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return new Date(iso).toLocaleDateString();
};

const iconForKind = (kind) => {
  if (kind === 'feed_reply') return <MessageSquare className="w-4 h-4 text-emerald-400" />;
  if (kind === 'feed_react') return <Heart className="w-4 h-4 text-blood-400" />;
  if (kind === 'friend_request') return <UserPlus className="w-4 h-4 text-amber-400" />;
  if (kind === 'friend_accepted') return <UserCheck className="w-4 h-4 text-emerald-400" />;
  if (kind === 'battle_challenge') return <Swords className="w-4 h-4 text-blood-400" />;
  if (kind === 'battle_accepted') return <Swords className="w-4 h-4 text-emerald-400" />;
  return <Bell className="w-4 h-4 text-stone-400" />;
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const pollRef = useRef(null);

  const authHeaders = () => {
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchUnread = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { headers: authHeaders() });
      setUnread(res.data.unread || 0);
    } catch (e) { /* silent */ }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications?limit=30`, { headers: authHeaders() });
      setItems(res.data.items || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  // Initial + polling
  useEffect(() => {
    // Only poll if logged in
    const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
    if (!token) return;
    fetchUnread();
    pollRef.current = setInterval(fetchUnread, POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchUnread]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      await fetchList();
    }
  };

  const handleMarkAllRead = async () => {
    if (unread === 0) return;
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { headers: authHeaders() });
      setUnread(0);
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) { /* silent */ }
  };

  const handleClear = async () => {
    try {
      await axios.delete(`${API}/notifications/clear`, { headers: authHeaders() });
      setItems([]);
      setUnread(0);
    } catch (e) { /* silent */ }
  };

  const handleClick = async (n) => {
    // Mark this one read optimistically and navigate
    if (!n.read) {
      setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
      try {
        await axios.post(`${API}/notifications/mark-read`, { ids: [n.id] }, { headers: authHeaders() });
      } catch (e) { /* silent */ }
    }
    setOpen(false);
    navigate(n.link || '/friends');
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="relative p-2 rounded-md text-stone-300 hover:text-white hover:bg-stone-800/60 transition-colors"
        aria-label="Notifications"
        data-testid="notifications-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-blood-600 text-white text-[10px] font-bold flex items-center justify-center border border-stone-950"
            data-testid="notifications-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 w-[320px] sm:w-[360px] max-h-[70vh] glass border border-stone-700 rounded-lg overflow-hidden shadow-2xl z-50"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-700 bg-stone-900/60">
            <div className="flex items-center gap-2">
              <Bell className="w-4 h-4 text-blood-400" />
              <span className="text-white font-bold tracking-wider text-sm">NOTIFICATIONS</span>
              {unread > 0 && (
                <span className="text-blood-400 text-xs font-semibold">({unread} new)</span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {items.length > 0 && unread > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="text-stone-400 hover:text-white text-[11px] uppercase tracking-wider"
                  data-testid="notifications-mark-all-read"
                >
                  Mark all read
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-stone-500 hover:text-white p-1"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="overflow-y-auto max-h-[55vh]">
            {loading && items.length === 0 ? (
              <div className="px-4 py-6 text-center text-stone-500 text-sm">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <Bell className="w-8 h-8 text-stone-600 mx-auto mb-2" />
                <p className="text-stone-400 text-sm">No notifications yet.</p>
                <p className="text-stone-600 text-xs mt-1">Reactions and replies will appear here.</p>
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleClick(n)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 border-b border-stone-800/60 hover:bg-stone-800/40 transition-colors ${
                    !n.read ? 'bg-blood-950/15' : ''
                  }`}
                  data-testid={`notification-item-${n.id}`}
                >
                  <div className="mt-0.5 shrink-0">{iconForKind(n.kind)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-stone-200 text-sm leading-snug break-words">{n.text}</p>
                    <p className="text-stone-500 text-[11px] mt-0.5">{formatTime(n.created_at)}</p>
                  </div>
                  {!n.read && (
                    <span className="w-2 h-2 rounded-full bg-blood-500 mt-2 shrink-0" aria-label="unread" />
                  )}
                </button>
              ))
            )}
          </div>

          {items.length > 0 && (
            <div className="border-t border-stone-700 bg-stone-900/40 px-4 py-2 flex justify-end">
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center gap-1 text-stone-500 hover:text-blood-400 text-[11px] uppercase tracking-wider"
                data-testid="notifications-clear-all"
              >
                <Trash2 className="w-3 h-3" />
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
